import React, { useEffect, useState } from 'react';
import { ArrowRight, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { SubscriberRow } from '../Subscribers/types';
import {
  fetchConnectionDashboardData,
  type ConnectionDashboardData,
  type StatusCountSummary,
} from '../../lib/connectionDashboard';
import { tierLabel } from '../../lib/personalization';
import {
  subscriberBestEmail,
  subscriberBestName,
  subscriberDetailPath,
  subscriberDisplayLabel,
} from '../../lib/subscriberDisplay';
import ConnectionTrendChart from './ConnectionTrendChart';

function loadErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'Failed to load network reach';
}

function relativeAddedLabel(createdAt: string | null | undefined): string {
  if (!createdAt) return '';
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function Last7DaysDelta({ count }: { count: number }) {
  return (
    <span className="text-[11px] text-gray-600 dark:text-gray-400">
      {count > 0 ? (
        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">+{count}</span>
      ) : (
        <span>0</span>
      )}{' '}
      in 7d
    </span>
  );
}

function StatusStatBlock({
  label,
  summary,
  emphasize = false,
}: {
  label: string;
  summary: StatusCountSummary;
  emphasize?: boolean;
}) {
  return (
    <div className={emphasize ? '' : 'pt-2 mt-2 border-t border-gray-200/80 dark:border-gray-700/80'}>
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div
        className={`mt-0.5 font-bold tabular-nums text-gray-900 dark:text-white leading-none ${
          emphasize ? 'text-2xl' : 'text-lg'
        }`}
      >
        {summary.total.toLocaleString()}
      </div>
      <div className="mt-0.5">
        <Last7DaysDelta count={summary.last7Days} />
      </div>
    </div>
  );
}

function ScoreBadge({ row }: { row: SubscriberRow }) {
  if (row.profile_score == null) {
    return (
      <span className="shrink-0 text-[10px] font-medium text-gray-400 dark:text-gray-500 tabular-nums">
        —
      </span>
    );
  }

  return (
    <span
      className="shrink-0 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
      title={
        row.profile_score_tier
          ? `Profile score · ${tierLabel(row.profile_score_tier)}`
          : 'Profile score'
      }
    >
      {row.profile_score}
    </span>
  );
}

const ConnectionsDashboardPanel: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ConnectionDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void fetchConnectionDashboardData()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(loadErrorMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-500" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Network Reach</h2>
        </div>
        <button
          type="button"
          onClick={() => navigate('/subscribers')}
          className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
        >
          View all
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Loading network reach…</div>
      ) : error ? (
        <div className="py-10 text-center text-sm text-rose-600 dark:text-rose-400">{error}</div>
      ) : data ? (
        <div className="flex flex-col md:flex-row gap-2 items-stretch">
          <div className="md:w-36 shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2.5">
            <StatusStatBlock
              label="Total"
              summary={{ total: data.total, last7Days: data.addedLast7Days }}
              emphasize
            />
            <StatusStatBlock label="Confirmed" summary={data.confirmed} />
          </div>

          <div className="md:w-56 shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 min-w-0 overflow-hidden">
            <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              Latest 5
            </div>
            {data.recent.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-2">No connections yet.</p>
            ) : (
              <ul className="divide-y divide-gray-200/80 dark:divide-gray-700/80">
                {data.recent.map((row) => {
                  const path = subscriberDetailPath(row);
                  const name = subscriberBestName(row);
                  const email = subscriberBestEmail(row);
                  const when = relativeAddedLabel(row.created_at);
                  const line = [subscriberDisplayLabel(row), when].filter(Boolean).join(' · ');
                  const title = [email && name ? email : email || null, typeof row.role === 'string' ? row.role.trim() : null]
                    .filter(Boolean)
                    .join(' · ');

                  return (
                    <li key={String(row.id ?? email)}>
                      <button
                        type="button"
                        disabled={!path}
                        title={title || undefined}
                        onClick={() => path && navigate(path)}
                        className="w-full flex items-center gap-2 py-1 text-left rounded hover:bg-white/80 dark:hover:bg-gray-800/80 transition-colors disabled:opacity-60 disabled:cursor-default"
                      >
                        <span className="min-w-0 flex-1 text-xs text-gray-900 dark:text-gray-100 truncate">
                          {line}
                        </span>
                        <ScoreBadge row={row} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex-1 min-w-0 self-stretch rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-0 overflow-hidden flex flex-col">
            <ConnectionTrendChart createdAtValues={data.createdAtValues} className="h-full min-h-0 flex-1" />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ConnectionsDashboardPanel;
